/* =====================================================================
 * StreamProgress — 실시간 에이전트 진행 상태 표시
 * ===================================================================== */

import React, { useMemo, useRef, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import styled, { keyframes, css } from 'styled-components';
import { ChevronDown, Globe, Check, Loader2, Wrench, X } from 'lucide-react';
import type {
  SearchResultsDetail,
  StreamEvent,
  TimelineActivityEntry,
  TimelineContentSegment,
  TimelineToolActivity,
} from '@auw/workspace-utils';
import {
  isInternalRootClassifierMessage,
  isTimelineChannel,
} from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* 제목 강조색(테넌트 포인트 컬러 brand)이 왼→오로 끊김 없이 sweep 한다.
 * background-size 200% 기준 position 130%(왼쪽 밖)→-30%(오른쪽 밖)이 좌→우
 * 한 사이클이며, 쉬는 구간 없이 곧바로 다음 사이클로 이어진다. */
const textShine = keyframes`
  from { background-position: 130% center; }
  to { background-position: -30% center; }
`;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: contents;
`;

const ToggleBtn = styled.button<{ $streaming?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#fff' : theme.colors.textSecondary};
  padding: 5px 12px 5px 6px;
  border-radius: 20px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.025)'};
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ $streaming }) =>
    $streaming &&
    css`
      border-color: ${({ theme }) => theme.colors.accent}30;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.6875rem;
    gap: 6px;
    padding: 4px 10px 4px 5px;
  }
`;

const StepBadge = styled.span<{ $streaming?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 0.6875rem;
  font-weight: 700;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    font-size: 0.625rem;
  }

  ${({ $streaming, theme }) =>
    $streaming
      ? css`
          background: ${theme.mode === 'dark' ? '#111111' : theme.colors.accent};
          color: ${theme.mode === 'dark' ? theme.colors.accent : '#fff'};
          border: 1px solid ${theme.mode === 'dark' ? `${theme.colors.accent}40` : 'transparent'};
        `
      : css`
          background: ${theme.mode === 'dark'
            ? '#111111'
            : 'rgba(0,0,0,0.07)'};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'transparent'};
        `}
`;

const ChevronWrap = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.6)' : theme.colors.textMuted};
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  animation: ${pulse} 1.2s ease-in-out infinite;
  flex-shrink: 0;
`;

/* "N개 단계 진행" 칩과 "Plan" 칩을 나란히 두는 헤더 행. */
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

/* Plan 칩의 단계 수 배지 (accent 계열로 steps 배지와 구분). */
const PlanBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 0.6875rem;
  font-weight: 700;
  box-sizing: border-box;
  background: ${({ theme }) => `${theme.colors.accent}22`};
  color: ${({ theme }) => theme.colors.accent};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    font-size: 0.625rem;
  }
`;

/* Plan 칩 펼침 시 체크리스트 박스 (검색 카드와 동일 시각 언어). */
const PlanPanel = styled.div`
  margin-top: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
`;

/* ---- Smooth collapse ---- */

const Collapsible = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  order: 1;
  flex-basis: 100%;
`;

const CollapsibleInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

/* ---- Timeline ---- */

/** 이 개수를 넘으면 스텝 패널을 스크롤 영역으로 전환 */
const MAX_VISIBLE_STEPS = 3;
/** 스텝 패널 스크롤 시 최대 높이(px) */
const STEP_PANEL_MAX_HEIGHT = 720;

const TimelineWrap = styled.div<{ $scrollable: boolean }>`
  padding: 8px 0 2px 0;
  margin-top: 6px;
  max-height: ${({ $scrollable }) =>
    $scrollable ? `${STEP_PANEL_MAX_HEIGHT}px` : 'none'};
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'visible')};
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  }
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &:hover {
    scrollbar-color: ${({ theme }) =>
        `${theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'} transparent`};
  }
`;


const DOT_CENTER_X = 20;   // 도트 중심 X 좌표
const DOT_TOP_Y = 11;      // 도트 중심 Y 좌표 (제목 첫 줄과 정렬)
const DOT_SIZE = 10;
const LINE_WIDTH = 2;

/* 스텝 = step_id 로 묶인 진행 단위. 제목 → 진행(narration) → (완료) 순서로
 * 세로 타임라인에 쌓인다. 본문이 있어 행 높이가 가변이므로 도트는 제목 첫
 * 줄에 정렬하고, 세로선은 도트~도트로 연결한다. */
const StepRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: flex-start;
  position: relative;
  padding: 4px 14px 4px ${DOT_CENTER_X + DOT_SIZE + 14}px;
  animation: ${fadeSlideIn} 0.25s ease forwards;

  /* ── 세로선 (::after) ── */
  &::after {
    content: '';
    position: absolute;
    left: ${DOT_CENTER_X - LINE_WIDTH / 2}px;
    top: 0;
    bottom: 0;
    width: ${LINE_WIDTH}px;
    background: ${({ theme }) => theme.colors.border};
    z-index: 0;
  }

  &:first-child::after {
    top: ${DOT_TOP_Y}px;
  }

  &:last-child::after {
    top: 0;
    bottom: auto;
    height: ${DOT_TOP_Y}px;
  }

  &:only-child::after {
    display: none;
  }

  /* ── 타임라인 도트 (::before) ── */
  &::before {
    content: '';
    position: absolute;
    left: ${DOT_CENTER_X - DOT_SIZE / 2}px;
    top: ${DOT_TOP_Y - DOT_SIZE / 2}px;
    width: ${DOT_SIZE}px;
    height: ${DOT_SIZE}px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent}40;
    border: 2px solid ${({ theme }) => theme.colors.accent}55;
    z-index: 2;
    transition: all 0.2s ease;
  }

  ${({ $active }) =>
    $active &&
    css`
      &::before {
        border-color: ${({ theme }) => theme.colors.accent};
        background: ${({ theme }) => theme.colors.accent};
        box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.accent}25;
        animation: ${pulse} 1.4s ease-in-out infinite;
      }
    `}
`;

const StepContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 4px 0 0;
`;

const stepTitleBase = css`
  min-width: 0;
  flex: 1 1 auto;
  font-size: 0.875rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8125rem;
    line-height: 1.4;
  }
`;

const StepTitle = styled.span`
  ${stepTitleBase}
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
`;

const StepActorTrail = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.6875rem;
  line-height: 1.35;
  letter-spacing: 0.01em;
`;

/* 진행 중 스텝 제목 — 테넌트 포인트 컬러(brand)가 기본 텍스트색 위로
 * 왼→오로 흐르는 shimmer. brand 는 b2c=주황 / 삼성=파랑처럼 테넌트
 * 매니페스트에서 주입되므로 하드코딩 없이 theme.colors.brand 만 쓴다. */
const StepTitleActive = styled.span`
  ${stepTitleBase}
  font-weight: 600;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.textPrimary} 0%,
    ${({ theme }) => theme.colors.textPrimary} 20%,
    ${({ theme }) => theme.colors.brand} 50%,
    ${({ theme }) => theme.colors.textPrimary} 80%,
    ${({ theme }) => theme.colors.textPrimary} 100%
  );
  background-size: 200% auto;
  color: ${({ theme }) => theme.colors.textPrimary};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${textShine} 4.5s linear infinite;
`;

/* 스텝 헤더(제목 줄) — 클릭 시 본문을 접고/펼친다. 본문이 있을 때만
 * 우측 chevron 을 노출하고 클릭 가능 상태가 된다. */
const StepHeader = styled.button<{ $clickable: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

/* 제목 우측 chevron — 동그라미 안에 ⌄. 스크롤바와 겹치지 않게 StepRow
 * 우측 패딩으로 여백을 확보한다. 회전은 안쪽 ChevronInner 가 담당. */
const StepChevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.55)' : theme.colors.textSecondary};
  transition: background 0.15s ease, color 0.15s ease;

  ${StepHeader}:hover & {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ChevronInner = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
`;

/** 본문 접힘 시 최대 높이(px). 이 높이를 넘으면 '더 보기'로 펼친다. */
const COLLAPSED_BODY_MAX_PX = 240;

const StepBody = styled.div<{ $clamped: boolean }>`
  font-size: 0.8125rem;
  line-height: 1.55;
  /* 추론(reasoning) 본문 가독성: textSecondary 기준 다크는 더 밝게,
   * 라이트는 더 어둡게 대비를 키운다(제목=textPrimary 보다는 약하게). */
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#d6d6d6' : '#45454f'};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
    line-height: 1.5;
  }

  ${({ $clamped }) =>
    $clamped &&
    css`
      max-height: ${COLLAPSED_BODY_MAX_PX}px;
      overflow: hidden;
      -webkit-mask-image: linear-gradient(to bottom, #000 52%, transparent 100%);
      mask-image: linear-gradient(to bottom, #000 52%, transparent 100%);
    `}
`;

const SegmentSlot = styled.div`
  & + & {
    margin-top: 8px;
  }
`;

const ProgressSegmentText = styled.div`
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#d6d6d6' : '#45454f'};
  white-space: pre-wrap;
`;

const ReasoningBlock = styled.div`
  overflow: hidden;
  border-left: 2px solid ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(168, 180, 255, 0.42)'
      : 'rgba(83, 99, 196, 0.42)'};
  border-radius: 0 7px 7px 0;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.045)'
      : 'rgba(44,55,105,0.045)'};
`;

const ReasoningHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-align: left;
`;

const ReasoningChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transition: transform 0.18s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
`;

const ReasoningText = styled.div`
  padding: 1px 9px 8px;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#b9bcc8' : '#626572'};
  font-size: 0.75rem;
  line-height: 1.55;
  white-space: pre-wrap;
`;

const ToolActivityBlock = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.1)'
      : 'rgba(30,70,130,0.14)'};
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.035)'
      : 'rgba(30,70,130,0.035)'};
`;

const ToolActivityHeader = styled.button<{ $clickable: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 7px;
  padding: 7px 9px;
  border: 0;
  background: transparent;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#d3d6df' : '#4f5665'};
  font: inherit;
  font-size: 0.75rem;
  line-height: 1.4;
  text-align: left;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const ToolStatusIcon = styled.span<{ $status: TimelineToolActivity['status'] }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${({ theme, $status }) =>
    $status === 'failed'
      ? theme.colors.error
      : $status === 'completed'
        ? theme.colors.accent
        : theme.colors.brand};

  ${({ $status }) =>
    $status === 'running' &&
    css`
      & svg {
        animation: ${spin} 0.9s linear infinite;
      }
    `}
`;

const ToolActivityLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
`;

const ToolActivityDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 1px 9px 9px 32px;
`;

const ToolDetailLabel = styled.div`
  margin-bottom: 2px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.6875rem;
  font-weight: 600;
`;

const ToolDetailValue = styled.pre`
  max-height: 180px;
  margin: 0;
  overflow: auto;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#b9bcc8' : '#626572'};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.6875rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
`;

/* '더 보기' / '접기' 토글 버튼 (추론 본문이 길 때만 노출). */
const MoreBtn = styled.button`
  align-self: flex-start;
  margin-top: 1px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---- 검색 결과 카드 ---- */

/* "결과 N개" 카운트 — 헤더 우측의 보조 라벨. */
const SearchCount = styled.span`
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 2px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.6875rem;
  }
`;

/* 결과 카드 목록 박스. 일정 개수 이상이면 내부 스크롤. */
const SearchCardList = styled.div`
  margin-top: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
  overflow: hidden;
  max-height: 196px;
  overflow-y: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
    border-radius: 3px;
  }
`;

/* 결과 한 건(클릭 시 새 탭). favicon + 제목(1줄) + 도메인(우측). */
const SearchCardRow = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  text-decoration: none;
  color: inherit;
  transition: background 0.12s ease;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
`;

const Favicon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
  object-fit: cover;
`;

const FaviconFallback = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CardTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDomain = styled.span`
  flex-shrink: 0;
  max-width: 38%;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;




/* ------------------------------------------------------------------ */
/* i18n-aware summary builder                                          */
/* ------------------------------------------------------------------ */

const MAX_PREVIEW = 80;
const truncate = (s: string, max = MAX_PREVIEW) =>
  s.length > max ? `${s.slice(0, max)}…` : s;

/** thought summary 본문에서 마크다운 잔여 기호와 tool-call 마크업을 정리한다.
 *
 * 모델(vLLM/Qwen)이 간헐적으로 흘리는 tool-call 마크업(`<tool_call>`, `<tool_code>`,
 * `<function=...>`, `<parameter=...>` 및 그 짝)은 qwen3_coder 파서가 못 먹은 잔여물로
 * 의미가 없으므로 타임라인 narration 에 노출하지 않는다. 이 마크업은 항상 narration
 * 끝에 붙으므로, 시작 토큰부터 끝까지를 잘라낸 뒤 혹시 남은 개별 태그도 제거한다. */
const cleanNarrationBody = (raw: string): string =>
  raw
    // tool-call 시도 블록(파서 미소비 잔여물)은 narration 끝에 붙으므로 그 지점부터 절단
    .replace(/<(?:tool_call|tool_code|function=|parameter=)[\s\S]*$/i, '')
    // 절단 후 혹시 남은 개별 tool-call 태그도 제거
    .replace(/<\/?(?:tool_call|tool_code|function|parameter)[^>]*>/gi, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/^\s*#{1,6}\s*/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** 본문이 제목으로 시작하면(thought lead-in 중복) 그 부분을 떼어낸다. */
const stripLeadingTitle = (body: string, title: string): string => {
  if (!body || !title) return body;
  const head = title.replace(/…$/, '').trim();
  if (!head) return body;
  const trimmed = body.replace(/^\s+/, '');
  if (trimmed.toLowerCase().startsWith(head.toLowerCase())) {
    return trimmed.slice(head.length).replace(/^[\s:.\-–—]+/, '').trim();
  }
  return body;
};

interface RenderStep {
  id: string;
  title: string;
  body: string;
  segments: TimelineContentSegment[];
  toolActivities: TimelineToolActivity[];
  timelineEntries: TimelineActivityEntry[];
  isActive: boolean;
  /** 채워져 있으면 이 스텝은 검색 결과 카드 블록으로 렌더한다. */
  searchResults?: SearchResultsDetail;
  /** 실행 계획 step id — 완료된 스텝에만 채워진다 (step_progress 종료 detail). */
  planStepId?: string;
  /** step_progress 라이프사이클 상태 (완료 판정의 권위 있는 신호). */
  stepStatus?: 'started' | 'completed' | 'failed';
  /** Source-authored handoff attribution shown below the semantic title. */
  actorTrail?: string;
}

/** URL 에서 표시용 도메인(www. 제거)을 추출. source 폴백용. */
const domainFromUrl = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/* favicon 이미지 — 로드 실패하거나 URL 이 없으면 Globe 아이콘으로 폴백. */
const FaviconIcon: React.FC<{ src?: string | null }> = ({ src }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <FaviconFallback>
        <Globe size={12} />
      </FaviconFallback>
    );
  }
  return <Favicon src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
};

/* ------------------------------------------------------------------ */
/* SearchStepItem — 검색 결과 카드 블록 (query 헤더 + 결과 리스트)     */
/* ------------------------------------------------------------------ */

const SearchStepItem: React.FC<{
  step: RenderStep;
  countLabel: string;
}> = ({ step, countLabel }) => {
  const sr = step.searchResults!;
  const [collapsed, setCollapsed] = useState(false);

  return (
    <StepRow $active={step.isActive}>
      <StepContent>
        <StepHeader
          type="button"
          $clickable
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          {step.isActive ? (
            <StepTitleActive>{step.title}</StepTitleActive>
          ) : (
            <StepTitle>{step.title}</StepTitle>
          )}
          <SearchCount>{countLabel}</SearchCount>
          <StepChevron>
            <ChevronInner $expanded={!collapsed}>
              <ChevronDown size={12} />
            </ChevronInner>
          </StepChevron>
        </StepHeader>

        {!collapsed && (
          <SearchCardList>
            {sr.results.map((r, i) => (
              <SearchCardRow
                key={`${step.id}-${i}`}
                href={r.url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                title={r.snippet || r.title}
              >
                <FaviconIcon src={r.favicon} />
                <CardTitle>{r.title}</CardTitle>
                <CardDomain>{r.source || domainFromUrl(r.url)}</CardDomain>
              </SearchCardRow>
            ))}
          </SearchCardList>
        )}
      </StepContent>
    </StepRow>
  );
};

const ReasoningSegmentItem: React.FC<{
  text: string;
  isActive: boolean;
  label: string;
}> = ({ text, isActive, label }) => {
  const [expanded, setExpanded] = useState(isActive);

  // Keep live reasoning open, then fold it as soon as the activity moves on.
  // A user can still reopen a completed block independently.
  useEffect(() => {
    setExpanded(isActive);
  }, [isActive]);

  return (
    <ReasoningBlock>
      <ReasoningHeader
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <ReasoningChevron $expanded={expanded}>
          <ChevronDown size={11} />
        </ReasoningChevron>
        {label}
      </ReasoningHeader>
      {expanded && <ReasoningText>{text}</ReasoningText>}
    </ReasoningBlock>
  );
};

const formatToolDetail = (value: unknown): string => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const ToolActivityItem: React.FC<{
  activity: TimelineToolActivity;
}> = ({ activity }) => {
  const { t } = useTranslation('common');
  const isLive = activity.status === 'running';
  const hasArgs =
    !!activity.args && Object.keys(activity.args).length > 0;
  const hasResult =
    activity.result !== undefined && activity.result !== null;
  const hasDetails = hasArgs || hasResult || !!activity.errorMessage;
  const [expanded, setExpanded] = useState(isLive && hasDetails);
  const toolLabel = activity.toolName
    ? resolveToolName(t, activity.toolName)
    : t('chat.timeline.toolActivityUnknownTool');

  useEffect(() => {
    setExpanded(isLive && hasDetails);
  }, [activity.status, hasDetails, isLive]);

  const label =
    activity.status === 'running'
      ? t('chat.timeline.toolActivityRunning', {
          agent: activity.agentName,
          tool: toolLabel,
        })
      : activity.status === 'failed'
        ? t('chat.timeline.toolActivityFailed', {
            agent: activity.agentName,
            tool: toolLabel,
          })
        : t('chat.timeline.toolActivityCompleted', {
            agent: activity.agentName,
            tool: toolLabel,
          });

  return (
    <ToolActivityBlock>
      <ToolActivityHeader
        type="button"
        $clickable={hasDetails}
        onClick={() => hasDetails && setExpanded((value) => !value)}
        aria-expanded={hasDetails ? expanded : undefined}
      >
        <ToolStatusIcon $status={activity.status}>
          {isLive ? (
            <Loader2 size={14} />
          ) : activity.status === 'failed' ? (
            <X size={13} strokeWidth={2.5} />
          ) : (
            <Wrench size={13} />
          )}
        </ToolStatusIcon>
        <ToolActivityLabel>{label}</ToolActivityLabel>
        {hasDetails && (
          <ReasoningChevron $expanded={expanded}>
            <ChevronDown size={11} />
          </ReasoningChevron>
        )}
      </ToolActivityHeader>
      {expanded && (
        <ToolActivityDetail>
          {hasArgs && (
            <div>
              <ToolDetailLabel>
                {t('chat.timeline.toolActivityInput')}
              </ToolDetailLabel>
              <ToolDetailValue>
                {formatToolDetail(activity.args)}
              </ToolDetailValue>
            </div>
          )}
          {hasResult && (
            <div>
              <ToolDetailLabel>
                {t('chat.timeline.toolActivityResult')}
              </ToolDetailLabel>
              <ToolDetailValue>
                {formatToolDetail(activity.result)}
              </ToolDetailValue>
            </div>
          )}
          {activity.errorMessage && (
            <div>
              <ToolDetailLabel>
                {t('chat.timeline.toolActivityError')}
              </ToolDetailLabel>
              <ToolDetailValue>{activity.errorMessage}</ToolDetailValue>
            </div>
          )}
        </ToolActivityDetail>
      )}
    </ToolActivityBlock>
  );
};

/* ------------------------------------------------------------------ */
/* StepItem — 스텝 1개 (제목 접기/펼치기 + 본문 더보기)               */
/* ------------------------------------------------------------------ */

const StepItem: React.FC<{
  step: RenderStep;
  moreLabel: string;
  lessLabel: string;
  reasoningLabel: string;
}> = ({ step, moreLabel, lessLabel, reasoningLabel }) => {
  const hasBody = !!step.body || step.timelineEntries.length > 0;
  /* 제목 chevron 으로 본문 전체 접기/펼치기 (기본 펼침) */
  const [collapsed, setCollapsed] = useState(false);
  /* 긴 본문 '더 보기'로 전체 노출 토글 (기본 접힘) */
  const [showFull, setShowFull] = useState(false);
  /* 본문이 접힘 한계를 넘는지 측정 결과 */
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  /* clamp 여부와 무관하게 scrollHeight 는 전체 높이를 반영하므로
   * 이 측정으로 '더 보기' 노출 여부를 안정적으로 판단한다. */
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || collapsed) return;
    setOverflowing(el.scrollHeight > COLLAPSED_BODY_MAX_PX + 12);
  }, [step.body, step.timelineEntries, step.toolActivities, collapsed]);

  const clamped = hasBody && !collapsed && !showFull && overflowing;

  return (
    <StepRow $active={step.isActive}>
      <StepContent>
        <StepHeader
          type="button"
          $clickable={hasBody}
          onClick={() => hasBody && setCollapsed((c) => !c)}
          aria-expanded={hasBody ? !collapsed : undefined}
        >
          {step.isActive ? (
            <StepTitleActive>{step.title}</StepTitleActive>
          ) : (
            <StepTitle>{step.title}</StepTitle>
          )}
          {hasBody && (
            <StepChevron>
              <ChevronInner $expanded={!collapsed}>
                <ChevronDown size={12} />
              </ChevronInner>
            </StepChevron>
          )}
        </StepHeader>

        {step.actorTrail && <StepActorTrail>{step.actorTrail}</StepActorTrail>}

        {hasBody && !collapsed && (
          <>
            <StepBody ref={bodyRef} $clamped={clamped}>
              {step.timelineEntries.length > 0
                ? step.timelineEntries.map((entry, index) => {
                    if (entry.kind === 'tool') {
                      const activity = step.toolActivities.find(
                        (candidate) =>
                          candidate.id === entry.activityId,
                      );
                      return activity ? (
                        <SegmentSlot
                          key={`${step.id}-tool-${entry.activityId}`}
                        >
                          <ToolActivityItem activity={activity} />
                        </SegmentSlot>
                      ) : null;
                    }
                    return (
                      <SegmentSlot key={`${step.id}-${entry.kind}-${index}`}>
                        {entry.kind === 'reasoning' ? (
                          <ReasoningSegmentItem
                            text={entry.text}
                            isActive={step.isActive}
                            label={reasoningLabel}
                          />
                        ) : (
                          <ProgressSegmentText>{entry.text}</ProgressSegmentText>
                        )}
                      </SegmentSlot>
                    );
                  })
                : step.body}
            </StepBody>
            {overflowing && (
              <MoreBtn type="button" onClick={() => setShowFull((s) => !s)}>
                {showFull ? lessLabel : moreLabel}
              </MoreBtn>
            )}
          </>
        )}
      </StepContent>
    </StepRow>
  );
};

function resolveToolName(t: TFunction, raw: string): string {
  const key = `chat.toolNames.${raw}`;
  const translated = t(key, { defaultValue: '' });
  if (translated && translated !== key) return translated;
  return raw.replace(/_/g, ' ');
}

function isSearchTool(name: string): boolean {
  return /search/i.test(name);
}

function buildLocalizedControlSummary(
  t: TFunction,
  evt: StreamEvent,
): string | null {
  const d = (
    evt.detail && typeof evt.detail === 'object' ? evt.detail : {}
  ) as Record<string, unknown>;
  const event = evt.event ?? '';
  const actorName = evt.actor?.name ?? '';

  if ((event as string) === 'output_registered') {
    const count = Number(d.registered_count ?? 0);
    if (d.has_errors) return t('chat.timeline.filesRegisteredError');
    if (count > 0) return t('chat.timeline.filesRegistered', { count });
  }

  const mode = String(d.mode ?? '').trim().toLowerCase();
  if (mode) {
    const modeKeys: Record<string, string> = {
      fast: 'chat.timeline.modeFastSelected',
      plan: 'chat.timeline.modePlanSelected',
      skill: 'chat.timeline.modeSkillSelected',
      direct: 'chat.timeline.modeDirectSelected',
      auto: 'chat.timeline.modeAutoSelected',
    };
    const modeKey = modeKeys[mode];
    if (modeKey) return t(modeKey);
    return t('chat.timeline.modeSelected', { mode });
  }

  const grade = String(d.grade ?? '').trim().toLowerCase();
  if (grade && (evt.phase === 'evaluation' || actorName === 'PlanEvaluator')) {
    return grade === 'pass'
      ? t('chat.timeline.evaluationPassed')
      : t('chat.timeline.evaluationNeedsRepair');
  }

  const action = String(d.action ?? '').trim().toLowerCase();
  if (action && (evt.phase === 'repair' || actorName === 'RepairPlanner')) {
    const repairKeys: Record<string, string> = {
      dirty_rerun: 'chat.timeline.repairDirtyRerun',
      replan: 'chat.timeline.repairReplan',
      recompose: 'chat.timeline.repairRecompose',
      fail_fast: 'chat.timeline.repairStopped',
    };
    const repairKey = repairKeys[action];
    if (repairKey) return t(repairKey);
  }

  if (
    actorName === 'ParallelExecution' ||
    evt.summary === 'Parallel execution update'
  ) {
    return t('chat.timeline.parallelExecutionUpdate');
  }

  return null;
}

const isRequestBootstrapEvent = (evt: StreamEvent): boolean =>
  evt.usageSeq === 0;

function buildLocalizedSummary(t: TFunction, evt: StreamEvent): string | null {
  const d = (evt.detail && typeof evt.detail === 'object' ? evt.detail : {}) as Record<string, unknown>;
  const event = evt.event ?? '';
  const actorName = evt.actor?.name ?? '';

  // 부트스트랩 마커(usageSeq===0): 라우팅(RootClassifier) 시작 직후 첫 타임라인 상태.
  // 백엔드가 usage 집계 시드로 한 번만 보내는 이벤트라 usageSeq===0 으로 유일하게 식별된다.
  if (isRequestBootstrapEvent(evt)) {
    return t('chat.timeline.understandingRequest');
  }

  const controlSummary = buildLocalizedControlSummary(t, evt);
  if (controlSummary) return controlSummary;

  if (event === 'agent_start') {
    const req = String(d.specialist_request ?? d.request ?? '').trim();
    if (req) return t('chat.timeline.askAgent', { agent: actorName, request: truncate(req) });
    return t('chat.timeline.agentStarted', { agent: actorName });
  }

  if (event === 'agent_end') {
    const res = String(d.result_text ?? '').trim();
    if (res) return t('chat.timeline.agentDone', { agent: actorName, result: truncate(res) });
    return t('chat.timeline.agentCompleted', { agent: actorName });
  }

  if (event === 'tool_start') {
    const toolRaw = String(d.tool_name ?? actorName ?? '').trim();
    const toolLabel = resolveToolName(t, toolRaw);
    if (isSearchTool(toolRaw)) return t('chat.timeline.searchRunning', { tool: toolLabel });
    return t('chat.timeline.toolRunning', { tool: toolLabel });
  }

  if (event === 'tool_end') {
    const toolRaw = String(d.tool_name ?? actorName ?? '').trim();
    const toolLabel = resolveToolName(t, toolRaw);
    if (isSearchTool(toolRaw)) return t('chat.timeline.searchDone', { tool: toolLabel });
    return t('chat.timeline.toolDone', { tool: toolLabel });
  }

  if (event === 'handoff') {
    const from = String(d.from_agent ?? '').trim();
    const to = actorName;
    if (from && to) return t('chat.timeline.handoff', { from, to });
  }

  if (event === 'error') {
    return t('chat.timeline.errorOccurred');
  }

  const status = String(d.status ?? '').trim();
  const workerName = String(d.worker_name ?? d.agent_name ?? '').trim();
  const title = String(d.title ?? '').trim();
  const resultText = String(d.result_text ?? '').trim();

  if (workerName && title && (status === 'started' || !status)) {
    return t('chat.timeline.stepStarted', { agent: workerName, title: truncate(title) });
  }
  if (workerName && status === 'completed') {
    if (resultText) return t('chat.timeline.stepDone', { agent: workerName, result: truncate(resultText) });
    if (title) return t('chat.timeline.stepDone', { agent: workerName, result: truncate(title) });
  }
  if (workerName && status === 'failed') {
    return t('chat.timeline.stepFailed', { agent: workerName, title: truncate(title || 'unknown') });
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* PlanChecklist — plan 모드 전체 계획 개요(체크리스트) 블록           */
/* ------------------------------------------------------------------ */

/** plan step 한 건의 진행 상태 */
export type PlanStatus = 'pending' | 'running' | 'done' | 'failed';

/** "Plan created" 이벤트(detail.execution_plan)에서 추린 step 요약 */
export interface PlanStepLite {
  stepId: string;
  title: string;
  objective: string;
  agentName: string;
}

/* 계획 항목 목록 박스 — 검색 카드 블록과 동일한 시각 언어(테두리+배경). */
const PlanBox = styled.div`
  margin-top: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
  overflow: hidden;
`;

const PlanRow = styled.div`
  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

/* 항목 헤더(상태 아이콘 + 제목 + chevron). objective 가 있으면 클릭 가능. */
const PlanRowHeader = styled.button<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  margin: 0;
  padding: 8px 12px;
  border: none;
  background: none;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.12s ease;

  &:hover {
    background: ${({ $clickable, theme }) =>
      $clickable
        ? theme.mode === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.03)'
        : 'transparent'};
  }
`;

const PlanItemTitle = styled.span<{ $running?: boolean; $done?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme, $done }) =>
    $done ? theme.colors.textSecondary : theme.colors.textPrimary};
  font-weight: ${({ $running }) => ($running ? 600 : 500)};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.75rem;
  }
`;

const planIconBase = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 50%;
  box-sizing: border-box;
`;

const PlanIconDone = styled.span`
  ${planIconBase}
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0d0d0d' : theme.colors.accent)};
  color: #fff;
`;

const PlanIconRunning = styled.span`
  ${planIconBase}
  color: ${({ theme }) => theme.colors.accent};

  & svg {
    animation: ${spin} 0.9s linear infinite;
  }
`;

const PlanIconFailed = styled.span`
  ${planIconBase}
  background: ${({ theme }) => theme.colors.error};
  color: #fff;
`;

const PlanIconPending = styled.span`
  ${planIconBase}
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  background: transparent;
`;

/* 항목 펼침 시 노출되는 objective 본문. 제목 글자와 좌측 정렬(12+16+9). */
const PlanObjective = styled.div`
  padding: 0 12px 10px 37px;
  font-size: 0.78rem;
  line-height: 1.5;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#cfcfcf' : '#55555f')};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.7rem;
    padding-left: 33px;
  }
`;

const PlanCheck: React.FC<{ status: PlanStatus }> = ({ status }) => {
  if (status === 'done') {
    return (
      <PlanIconDone>
        <Check size={11} strokeWidth={3} />
      </PlanIconDone>
    );
  }
  if (status === 'running') {
    return (
      <PlanIconRunning>
        <Loader2 size={13} />
      </PlanIconRunning>
    );
  }
  if (status === 'failed') {
    return (
      <PlanIconFailed>
        <X size={11} strokeWidth={3} />
      </PlanIconFailed>
    );
  }
  return <PlanIconPending />;
};

/* 계획 항목 1개 — 상태 아이콘 + 제목, 클릭 시 objective 펼침. */
export const PlanItem: React.FC<{ step: PlanStepLite; status: PlanStatus }> = ({
  step,
  status,
}) => {
  const [open, setOpen] = useState(false);
  const hasObjective = !!step.objective.trim();
  return (
    <PlanRow>
      <PlanRowHeader
        type="button"
        $clickable={hasObjective}
        onClick={() => hasObjective && setOpen((o) => !o)}
        aria-expanded={hasObjective ? open : undefined}
      >
        <PlanCheck status={status} />
        <PlanItemTitle $running={status === 'running'} $done={status === 'done'}>
          {step.title}
        </PlanItemTitle>
        {hasObjective && (
          <StepChevron>
            <ChevronInner $expanded={open}>
              <ChevronDown size={12} />
            </ChevronInner>
          </StepChevron>
        )}
      </PlanRowHeader>
      {hasObjective && open && <PlanObjective>{step.objective}</PlanObjective>}
    </PlanRow>
  );
};

/* 전체 계획 개요 블록 — 타임라인 첫 노드로 노출(헤더 클릭 시 접기/펼치기). */
export const PlanChecklist: React.FC<{
  steps: PlanStepLite[];
  statusOf: (step: PlanStepLite) => PlanStatus;
  label: string;
}> = ({ steps, statusOf, label }) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <StepRow>
      <StepContent>
        <StepHeader
          type="button"
          $clickable
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          <StepTitle>{label}</StepTitle>
          <StepChevron>
            <ChevronInner $expanded={!collapsed}>
              <ChevronDown size={12} />
            </ChevronInner>
          </StepChevron>
        </StepHeader>

        {!collapsed && (
          <PlanBox>
            {steps.map((s) => (
              <PlanItem key={s.stepId || s.title} step={s} status={statusOf(s)} />
            ))}
          </PlanBox>
        )}
      </StepContent>
    </StepRow>
  );
};

/** 이벤트 스트림에서 가장 최신 execution_plan 을 찾아 step 요약 목록으로 변환.
 *  replan 으로 새 플랜이 오면 가장 마지막 것을 사용한다. plan 이벤트가 없으면 null. */
export const parsePlan = (events: StreamEvent[]): PlanStepLite[] | null => {
  for (let i = events.length - 1; i >= 0; i--) {
    const detail = events[i].detail;
    if (!detail || typeof detail !== 'object') continue;
    const ep = (detail as Record<string, unknown>).execution_plan;
    if (!ep || typeof ep !== 'object') continue;
    const rawSteps = (ep as Record<string, unknown>).steps;
    if (!Array.isArray(rawSteps) || rawSteps.length === 0) continue;
    const parsed = rawSteps
      .map((s) => {
        const o = (s ?? {}) as Record<string, unknown>;
        return {
          stepId: String(o.step_id ?? '').trim(),
          title: String(o.title ?? '').trim(),
          objective: String(o.objective ?? '').trim(),
          agentName: String(o.agent_name ?? '').trim(),
        };
      })
      .filter((s) => s.title || s.stepId);
    if (parsed.length > 0) return parsed;
  }
  return null;
};

/* ------------------------------------------------------------------ */
/* 파생 로직 — events → 타임라인 스텝 / plan step 상태.                  */
/* ------------------------------------------------------------------ */

const isVisibleTimelineEvent = (event: StreamEvent): boolean => {
  // A root classifier is a schema-only router. Its partial RouteDecision
  // JSON must not render even when an older server marked it visible.
  if (isInternalRootClassifierMessage(event.event, event.actor?.name)) return false;
  // `not_applied` is protocol state for a read-only document turn, not a
  // user-facing progress step. The model-authored read-only answer is
  // rendered by the message body; localized mutation-status titles must not
  // become a second sentence above it.
  if (event.documentMutationStatus === 'not_applied') return false;
  if (typeof event.visibleInTimeline === 'boolean') return event.visibleInTimeline;
  return isTimelineChannel(event.channel, event.event, event.actor?.name);
};

const isExecutionPlanEvent = (event: StreamEvent): boolean =>
  !!event.detail &&
  typeof event.detail === 'object' &&
  'execution_plan' in (event.detail as Record<string, unknown>);

const buildActivityFallbackTitle = (t: TFunction, event: StreamEvent): string => {
  const kind =
    event.activityKind ??
    (event.phase === 'routing'
      ? 'routing'
      : event.phase === 'planning'
        ? 'planning'
        : event.phase === 'evaluation'
          ? 'evaluation'
          : event.phase === 'repair' || event.phase === 'refinement'
            ? 'repair'
            : event.phase === 'final'
              ? 'answer'
              : 'execution');
  return t(`chat.timeline.activity.${kind}`);
};

const buildActorTrail = (event: StreamEvent): string | undefined => {
  const source = event.sourceActor?.name?.trim();
  const target = event.targetActor?.name?.trim();
  if (source && target && source !== target) return `${source} → ${target}`;
  return target || event.actor?.name?.trim() || source || undefined;
};

/**
 * Whether every rendered timeline row has stopped progressing.
 *
 * Unified/DOCX timeline rows are scoped to the parent message and are
 * terminal once that message stops streaming. XLSX client-side apply is the
 * exception: it can remain explicitly `pending` after the response finishes,
 * so the collapsed pill must keep its progress label until CAS finalization.
 */
export const isTimelineComplete = (
  events: StreamEvent[],
  isStreaming: boolean,
): boolean => {
  if (isStreaming) return false;

  const visibleTimelineEvents = events.filter(
    (event) => isVisibleTimelineEvent(event) && !isExecutionPlanEvent(event),
  );
  return (
    visibleTimelineEvents.length > 0 &&
    visibleTimelineEvents.every(
      (event) => event.documentMutationStatus !== 'pending',
    )
  );
};

const buildRenderSegments = (
  event: StreamEvent,
  title: string,
  fallbackBody: string,
): TimelineContentSegment[] => {
  const source =
    event.segments && event.segments.length > 0
      ? event.segments
      : fallbackBody
        ? [{
            kind:
              event.contentKind ??
              (event.stepStatus === 'started'
                ? 'progress'
                : event.event === 'thinking'
                  ? 'reasoning'
                  : 'progress'),
            text: fallbackBody,
          } satisfies TimelineContentSegment]
        : [];
  const result: TimelineContentSegment[] = [];
  let isFirstVisibleSegment = true;

  source.forEach((segment) => {
    let text = cleanNarrationBody(segment.text);
    if (isFirstVisibleSegment) {
      text = stripLeadingTitle(text, title);
    }
    if (!text) return;
    isFirstVisibleSegment = false;
    const previous = result[result.length - 1];
    if (previous?.kind === segment.kind) {
      previous.text = `${previous.text}\n\n${text}`;
    } else {
      result.push({ kind: segment.kind, text });
    }
  });
  return result;
};

const buildRenderEntries = (
  event: StreamEvent,
  title: string,
  fallbackSegments: TimelineContentSegment[],
): TimelineActivityEntry[] => {
  const source =
    event.timelineEntries && event.timelineEntries.length > 0
      ? event.timelineEntries
      : [
          ...fallbackSegments,
          ...(event.toolActivities ?? []).map(
            (activity) =>
              ({
                kind: 'tool',
                activityId: activity.id,
              }) satisfies TimelineActivityEntry,
          ),
        ];
  const result: TimelineActivityEntry[] = [];
  let isFirstVisibleText = true;

  source.forEach((entry) => {
    if (entry.kind === 'tool') {
      if (
        !result.some(
          (current) =>
            current.kind === 'tool' &&
            current.activityId === entry.activityId,
        )
      ) {
        result.push({ ...entry });
      }
      return;
    }

    let text = cleanNarrationBody(entry.text);
    if (isFirstVisibleText) {
      text = stripLeadingTitle(text, title);
    }
    if (!text) return;
    isFirstVisibleText = false;
    const previous = result[result.length - 1];
    if (previous?.kind === entry.kind) {
      previous.text = `${previous.text}\n\n${text}`;
    } else {
      result.push({ kind: entry.kind, text });
    }
  });
  return result;
};

/** events → 타임라인 렌더 스텝 목록. (plan 이벤트는 헤더의 Plan 칩으로 별도 노출하므로 제외) */
export const deriveTimelineSteps = (
  events: StreamEvent[],
  isStreaming: boolean,
  t: TFunction,
): RenderStep[] => {
  const filteredEvents = events.filter(isVisibleTimelineEvent);
  const out: RenderStep[] = [];
  filteredEvents.forEach((evt) => {
    if (isExecutionPlanEvent(evt)) return;
    const isRequestBootstrap = isRequestBootstrapEvent(evt);

    if (evt.event === 'search_results' && evt.detail && typeof evt.detail === 'object') {
      const sr = evt.detail as SearchResultsDetail;
      if (Array.isArray(sr.results) && sr.results.length > 0) {
        out.push({
          id: evt.id,
          title: (sr.query || '').trim() || t('chat.timeline.searchResults'),
          body: '',
          segments: [],
          toolActivities: [],
          timelineEntries: [],
          isActive: false,
          searchResults: sr,
        });
      }
      return;
    }

    const hasStep = typeof evt.stepId === 'string' && !!evt.stepId;

    let bodyText =
      evt.segments && evt.segments.length > 0
        ? evt.segments.map((segment) => segment.text).join('\n\n')
        : '';
    if (!bodyText && evt.detail && typeof evt.detail === 'object') {
      const d = evt.detail as Record<string, unknown>;
      bodyText = String(d.text ?? d.result_text ?? '').trim();
    } else if (!bodyText && typeof evt.detail === 'string') {
      const s = evt.detail.trim();
      if (hasStep || s.length > 60) bodyText = s;
    }
    bodyText = cleanNarrationBody(bodyText);

    let title = '';
    let body = '';
    if (isRequestBootstrap) {
      title =
        buildLocalizedSummary(t, evt) ||
        t('chat.timeline.understandingRequest');
    } else if (hasStep) {
      const localizedControlTitle = buildLocalizedControlSummary(t, evt);
      title =
        localizedControlTitle ||
        (typeof evt.title === 'string' && evt.title.trim()) ||
        buildActivityFallbackTitle(t, evt) ||
        (typeof evt.summary === 'string' ? evt.summary.trim() : '');
      body = stripLeadingTitle(bodyText, title);
    } else if (bodyText) {
      const localized = buildLocalizedSummary(t, evt);
      title = (evt.actor?.name ?? '').trim() || localized || (evt.event ?? '');
      body = stripLeadingTitle(bodyText, title) || bodyText;
    } else {
      const localized = buildLocalizedSummary(t, evt);
      title =
        localized ||
        (typeof evt.summary === 'string' && evt.summary.trim().length > 0
          ? evt.summary.trim()
          : evt.actor?.name
            ? `${evt.actor.name} ${evt.event || ''}`.trim()
            : evt.event || '');
    }

    const segments = isRequestBootstrap
      ? []
      : buildRenderSegments(evt, title, body);
    const timelineEntries = isRequestBootstrap
      ? []
      : buildRenderEntries(evt, title, segments);
    const orderedSegments = timelineEntries.filter(
      (entry): entry is TimelineContentSegment => entry.kind !== 'tool',
    );
    if (orderedSegments.length > 0) {
      body = orderedSegments.map((segment) => segment.text).join('\n\n');
    }
    if (!title && !body && timelineEntries.length === 0) return;
    out.push({
      id: evt.id,
      title: title || body,
      body: title ? body : '',
      segments: title ? orderedSegments : [],
      toolActivities: evt.toolActivities ?? [],
      timelineEntries: title ? timelineEntries : [],
      isActive: false,
      planStepId: evt.planStepId ?? undefined,
      stepStatus: evt.stepStatus ?? undefined,
      actorTrail: isRequestBootstrap ? undefined : buildActorTrail(evt),
    });
  });
  if (out.length > 0 && isStreaming) {
    out[out.length - 1].isActive = true;
  }
  return out;
};

/**
 * plan step 상태 판정기.
 *
 * 완료는 백엔드의 step_progress 종료 신호(`planStepId` + `stepStatus`)로
 * 판정한다. 이게 유일하게 권위 있는 신호다 — 제목 대조만 쓰면
 *   - 방금 끝난 스텝이 다음 스텝 시작 전까지 계속 running 으로 보이고,
 *   - 병렬 실행 시 아직 도는 스텝이 done 으로 보인다.
 *
 * 최신 서버의 started/terminal 이벤트는 모두 plan step id 를 싣는다. 제목
 * 대조는 해당 typed lifecycle이 없는 구버전 이벤트에만 사용한다.
 */
export const derivePlanStatusOf = (
  steps: RenderStep[],
  isStreaming: boolean,
): ((step: PlanStepLite) => PlanStatus) => {
  const renderable = steps.filter((s) => !s.searchResults);
  const latestStatusByPlanStepId = new Map<
    string,
    'started' | 'completed' | 'failed'
  >();
  const terminalTitles = new Map<string, 'completed' | 'failed'>();
  renderable.forEach((s) => {
    if (s.planStepId && s.stepStatus) {
      latestStatusByPlanStepId.set(s.planStepId, s.stepStatus);
    } else if (
      s.title &&
      (s.stepStatus === 'completed' || s.stepStatus === 'failed')
    ) {
      terminalTitles.set(s.title, s.stepStatus);
    }
  });

  const legacyRenderable = renderable.filter((s) => !s.planStepId);
  const seen = new Set(legacyRenderable.map((s) => s.title));
  /* typed lifecycle이 없는 구버전 행 중 가장 마지막 것이 "실행 중". */
  const stillRunning = renderable.filter(
    (s) =>
      !s.planStepId &&
      s.stepStatus !== 'completed' &&
      s.stepStatus !== 'failed',
  );
  const runningTitle =
    isStreaming && stillRunning.length > 0
      ? stillRunning[stillRunning.length - 1].title
      : undefined;

  return (step: PlanStepLite): PlanStatus => {
    if (step.stepId && latestStatusByPlanStepId.has(step.stepId)) {
      const status = latestStatusByPlanStepId.get(step.stepId);
      if (status === 'failed') return 'failed';
      if (status === 'completed') return 'done';
      return isStreaming ? 'running' : 'failed';
    }
    if (step.title && terminalTitles.has(step.title)) {
      return terminalTitles.get(step.title) === 'failed' ? 'failed' : 'done';
    }
    if (step.title && step.title === runningTitle) return 'running';
    if (seen.has(step.title)) return 'done';
    return 'pending';
  };
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface Props {
  events: StreamEvent[];
  isStreaming: boolean;
  hasContent?: boolean;
}

export const StreamProgress: React.FC<Props> = ({ events, isStreaming, hasContent }) => {
  /* 이전 대화 진입 시 이미 답변이 있으면 접힌 상태로 시작 */
  const [expanded, setExpanded] = React.useState(!hasContent);

  /* plan 칩 토글 — 플랜 생성 시(실행 중) 기본 펼침, 히스토리(답변 완료)는 접힘.
     사용자가 직접 접/펼침 가능. */
  const [planExpanded, setPlanExpanded] = React.useState(!hasContent);

  /* 타임라인 스크롤 컨테이너 ref */
  const timelineRef = useRef<HTMLDivElement>(null);

  /* 페이드 마스크 상태 */
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);

  /* 스트리밍 중 최종 답변이 나타나면 자동으로 접기 */
  const prevHasContent = React.useRef(!!hasContent);
  React.useEffect(() => {
    if (hasContent && !prevHasContent.current) {
      setExpanded(false);
    }
    prevHasContent.current = !!hasContent;
  }, [hasContent]);

  const { t } = useTranslation('common');
  const moreLabel = t('chat.showMore');
  const lessLabel = t('chat.showLess');

  /* plan 모드: 전체 계획(체크리스트) 소스. plan 이벤트가 없으면 null. */
  const planSteps = useMemo(() => parsePlan(events), [events]);

  /* 이벤트 → 스텝(제목 + 전체 본문) 모델로 변환. (공유 파생 로직) */
  const steps = useMemo<RenderStep[]>(
    () => deriveTimelineSteps(events, isStreaming, t),
    [events, isStreaming, t],
  );

  const timelineComplete = useMemo(
    () => isTimelineComplete(events, isStreaming),
    [events, isStreaming],
  );

  /* plan step 상태 판정기 (공유 파생 로직) */
  const planStatusOf = useMemo(
    () => derivePlanStatusOf(steps, isStreaming),
    [steps, isStreaming],
  );

  const scrollable = steps.length > MAX_VISIBLE_STEPS;

  /* 새 스텝 추가 시 하단으로 자동 스크롤 (최신 스텝이 보이도록) */
  useEffect(() => {
    const el = timelineRef.current;
    if (el && expanded) {
      el.scrollTop = el.scrollHeight;
    }
  }, [steps.length, expanded]);

  /* 스크롤 위치 추적 → 상/하단 페이드 마스크 제어 */
  useEffect(() => {
    const el = timelineRef.current;
    if (!el || !scrollable) {
      setFadeTop(false);
      setFadeBottom(false);
      return;
    }
    const check = () => {
      setFadeTop(el.scrollTop > 4);
      setFadeBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    };
    el.addEventListener('scroll', check, { passive: true });
    check();
    return () => el.removeEventListener('scroll', check);
  }, [scrollable, steps.length, expanded]);

  /* 페이드 마스크 스타일 (CSS mask-image) */
  const fadeMask = useMemo<React.CSSProperties | undefined>(() => {
    if (!scrollable || (!fadeTop && !fadeBottom)) return undefined;
    const F = 28; // fade zone px
    let grad: string;
    if (fadeTop && fadeBottom) {
      grad = `linear-gradient(to bottom, transparent 0%, black ${F}px, black calc(100% - ${F}px), transparent 100%)`;
    } else if (fadeTop) {
      grad = `linear-gradient(to bottom, transparent 0%, black ${F}px)`;
    } else {
      grad = `linear-gradient(to bottom, black calc(100% - ${F}px), transparent 100%)`;
    }
    return { WebkitMaskImage: grad, maskImage: grad };
  }, [scrollable, fadeTop, fadeBottom]);

  if (steps.length === 0 && !planSteps) return null;

  return (
    <Container>
      {/* 헤더: 왼쪽 "N개 단계 진행" 칩 + 오른쪽 "플랜 · N단계" 칩(동일 UI) */}
      <HeaderRow>
        <ToggleBtn $streaming={isStreaming} onClick={() => setExpanded(!expanded)}>
          <StepBadge $streaming={isStreaming}>{steps.length}</StepBadge>
          {timelineComplete
            ? t('button.done')
            : t('chat.stepsInProgress', { count: steps.length })}
          {isStreaming && <ActiveDot />}
          <ChevronWrap $expanded={expanded}>
            <ChevronDown size={14} />
          </ChevronWrap>
        </ToggleBtn>

        {planSteps && (
          <ToggleBtn
            type="button"
            $streaming={planExpanded}
            onClick={() => setPlanExpanded((v) => !v)}
            aria-expanded={planExpanded}
          >
            <PlanBadge>{planSteps.length}</PlanBadge>
            {t('chat.timeline.planOverview', { count: planSteps.length })}
            <ChevronWrap $expanded={planExpanded}>
              <ChevronDown size={14} />
            </ChevronWrap>
          </ToggleBtn>
        )}
      </HeaderRow>

      {/* Plan 칩 펼침 영역 — 플랜 체크리스트 */}
      {planSteps && (
        <Collapsible $expanded={planExpanded}>
          <CollapsibleInner>
            <PlanPanel>
              {planSteps.map((s) => (
                <PlanItem key={s.stepId || s.title} step={s} status={planStatusOf(s)} />
              ))}
            </PlanPanel>
          </CollapsibleInner>
        </Collapsible>
      )}

      {/* "N개 단계 진행" 펼침 영역 — 실행 타임라인 */}
      <Collapsible $expanded={expanded}>
        <CollapsibleInner>
          <TimelineWrap ref={timelineRef} $scrollable={scrollable} style={fadeMask}>
            {steps.map((step) =>
              step.searchResults ? (
                <SearchStepItem
                  key={step.id}
                  step={step}
                  countLabel={t('chat.timeline.searchResultCount', {
                    count: step.searchResults.count,
                  })}
                />
              ) : (
                <StepItem
                  key={step.id}
                  step={step}
                  moreLabel={moreLabel}
                  lessLabel={lessLabel}
                  reasoningLabel={t('chat.reasoningProcess')}
                />
              ),
            )}
          </TimelineWrap>
        </CollapsibleInner>
      </Collapsible>
    </Container>
  );
};
